import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  checkboxClass,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getPtDischargeNonVisitForm,
  updatePtDischargeNonVisitForm,
} from "../../../../../services/patientService";
import {
  AlertCircleIcon,
  CommentsIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HelpingIcon,
  LogOutIcon,
  MedicalNotesIcon,
  PillIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  PT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import { USER_ROLES } from "../../../../../constants";
import {
  ASSISTIVE_DEVICE_OPTIONS,
  CAREGIVER_COMM_METHOD_OPTIONS,
  DISCHARGE_DISPOSITION_OPTIONS,
  DISCHARGE_INSTRUCTION_TOPIC_OPTIONS,
  DISCHARGE_REASON_OPTIONS,
  FUNCTIONAL_DOMAIN_OPTIONS,
  FUNCTIONAL_TREND_OPTIONS,
  MEDICATION_RECONCILE_OPTIONS,
  NON_VISIT_REASON_OPTIONS,
  OASIS_APPLICABILITY_OPTIONS,
  OASIS_ASSESSMENT_STATUS_OPTIONS,
  OASIS_SUBMISSION_OPTIONS,
  OUTSTANDING_TREATMENT_OPTIONS,
  PATIENT_CONTACTED_OPTIONS,
  PHYSICIAN_NOTIFY_METHOD_OPTIONS,
  PLAN_OF_CARE_GOAL_OPTIONS,
  PRIOR_INSTRUCTION_OPTIONS,
  PT_DNV_ARRAY_KEYS,
  TRANSITION_INFO_OPTIONS,
  UNDERSTANDING_OPTIONS,
  YES_NO_NOT_REQUIRED_OPTIONS,
  emptyFunctionalStatusRows,
  emptyPtDischargeNonVisitPayload,
  toggleInList,
  today,
} from "./ptDischargeNonVisitContent";
import {
  assertPtVisitMetaForFlag,
  buildPtVisitMetaProps,
  pickPtVisitMeta,
} from "./ptVisitCommon";
import PtNotesCommonHeader from "./PtNotesCommonHeader";
import {
  normalizePtCommonHeaderState,
  resolvePtNotesCommonHeader,
  withNormalizedPtCommonHeader,
} from "./ptNotesCommonHeaderUtils";

const DISCHARGE_DATE_MAX = new Date().toISOString().slice(0, 10);

const UNABLE_TO_ASSESS_GOAL =
  "Unable to assess due to non-visit discharge";

const OASIS_NOT_REQUIRED = "OASIS discharge assessment not required";

const OASIS_CMS_CALLOUT =
  "IMPORTANT: This non-visit discharge form does not replace a required OASIS discharge assessment visit when an OASIS discharge assessment is required under CMS guidance. Complete applicable OASIS time points and submissions per current CMS instructions (including OASIS-E2 effective 04/01/2026 and later).";

const LEGACY_PAYLOAD_KEYS = [
  "documentationDate",
  "discipline",
  "disciplineOther",
  "clinicianName",
  "credentials",
  "generalCondition",
  "painStatus",
  "mobilityStatus",
  "cognitiveStatus",
  "respiratoryStatus",
  "otherConcerns",
  "physicianNotifyFlags",
  "physicianNotifyMethod",
  "caregiverCommFlags",
  "caregiverCommMethod",
  "caregiverInstructions",
  "caregiverUnderstood",
  "outstandingOrders",
  "transitionHandoffProvided",
  "oasisStatus",
  "documentationReview",
  "attestationClinicianName",
  "attestationDate",
  "attestationCredentials",
  "socDate",
  "mrn",
  "dateOfBirth",
];

const emptyValues = () => ({
  ...emptyPtDischargeNonVisitPayload(),
  dischargeDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const normalizeExclusiveYesNoNotRequired = (value) => {
  if (Array.isArray(value)) {
    const match = value
      .map((item) => String(item || "").trim())
      .find((item) => YES_NO_NOT_REQUIRED_OPTIONS.includes(item));
    return match || "";
  }
  const s = String(value || "").trim();
  if (YES_NO_NOT_REQUIRED_OPTIONS.includes(s)) return s;
  // Legacy casing
  if (s === "Not Required") return "Not required";
  return "";
};

const normalizeExclusiveOption = (value, options) => {
  if (Array.isArray(value)) {
    const match = value
      .map((item) => String(item || "").trim())
      .find((item) => options.includes(item));
    return match || "";
  }
  const s = String(value || "").trim();
  return options.includes(s) ? s : s;
};

const normalizeDisciplinePt = (loadedPayload) => {
  if (typeof loadedPayload.disciplinePt === "boolean") {
    return loadedPayload.disciplinePt;
  }
  if (Array.isArray(loadedPayload.discipline)) {
    return loadedPayload.discipline.map(String).includes("PT");
  }
  return true;
};

const normalizeFunctionalStatus = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  const byDomain = Object.fromEntries(
    list
      .filter((row) => row && row.domain)
      .map((row) => [String(row.domain), row]),
  );
  return FUNCTIONAL_DOMAIN_OPTIONS.map((domain) => {
    const existing = byDomain[domain] || {};
    const trend = String(existing.trend || "").trim();
    return {
      domain,
      trend: FUNCTIONAL_TREND_OPTIONS.includes(trend) ? trend : "",
    };
  });
};

const asStringList = (value) => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value == null || value === "") return [];
  return [String(value)];
};

const migrateLegacyArrays = (loadedPayload) => {
  const next = {};

  if (
    !Array.isArray(loadedPayload.physicianNotifyMethods) &&
    (loadedPayload.physicianNotifyMethod ||
      Array.isArray(loadedPayload.physicianNotifyFlags))
  ) {
    const methods = [
      ...asStringList(loadedPayload.physicianNotifyMethods),
      ...asStringList(loadedPayload.physicianNotifyMethod),
      ...asStringList(loadedPayload.physicianNotifyFlags).filter((item) =>
        PHYSICIAN_NOTIFY_METHOD_OPTIONS.includes(item),
      ),
    ];
    next.physicianNotifyMethods = [...new Set(methods)];
  }

  if (
    !Array.isArray(loadedPayload.caregiverCommMethods) &&
    (loadedPayload.caregiverCommMethod ||
      Array.isArray(loadedPayload.caregiverCommFlags))
  ) {
    const methods = [
      ...asStringList(loadedPayload.caregiverCommMethods),
      ...asStringList(loadedPayload.caregiverCommMethod),
      ...asStringList(loadedPayload.caregiverCommFlags).filter((item) =>
        CAREGIVER_COMM_METHOD_OPTIONS.includes(item),
      ),
    ];
    next.caregiverCommMethods = [...new Set(methods)];
  }

  if (
    !Array.isArray(loadedPayload.caregiverUnderstanding) &&
    loadedPayload.caregiverUnderstood != null
  ) {
    next.caregiverUnderstanding = loadedPayload.caregiverUnderstood
      ? ["Verbalized understanding"]
      : [];
  }

  if (
    !Array.isArray(loadedPayload.oasisAssessmentStatus) &&
    loadedPayload.oasisStatus
  ) {
    const status = String(loadedPayload.oasisStatus || "").trim();
    next.oasisAssessmentStatus = status ? [status] : [];
  }

  if (
    !Array.isArray(loadedPayload.oasisApplicability) &&
    String(loadedPayload.oasisStatus || "")
      .toLowerCase()
      .includes("not required")
  ) {
    next.oasisApplicability = [OASIS_NOT_REQUIRED];
  }

  return next;
};

const stripLegacyKeys = (payload) => {
  const next = { ...(payload || {}) };
  LEGACY_PAYLOAD_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

const PlanOfCareGoalChoice = ({ value, canEdit, onChange }) => (
  <div className="grid grid-cols-1 gap-1.5" role="radiogroup" aria-label="planOfCareGoalStatus">
    {PLAN_OF_CARE_GOAL_OPTIONS.map((opt) => {
      const checked = value === opt;
      const isUnable = opt === UNABLE_TO_ASSESS_GOAL;
      return (
        <label
          key={opt}
          className={`flex items-center gap-2 text-[12px] rounded-md border px-2.5 py-2 ${
            canEdit ? "cursor-pointer" : "cursor-default"
          } ${
            checked
              ? "border-indigo-300 bg-indigo-50/60"
              : "border-slate-200 bg-white"
          }`}
        >
          <input
            type="radio"
            name="planOfCareGoalStatus"
            className="h-3.5 w-3.5 shrink-0 border-slate-300 text-indigo-500"
            checked={checked}
            disabled={!canEdit}
            onChange={() => onChange(opt)}
            onClick={(e) => {
              if (canEdit && checked) {
                e.preventDefault();
                onChange("");
              }
            }}
          />
          <span
            className={`leading-snug ${
              isUnable ? "font-semibold text-red-600" : "text-slate-700"
            }`}
          >
            {opt}
          </span>
        </label>
      );
    })}
  </div>
);

PlanOfCareGoalChoice.propTypes = {
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const OasisApplicabilityGrid = ({ values, canEdit, onToggle }) => {
  const selected = Array.isArray(values) ? values : [];
  return (
    <div className="grid grid-cols-1 gap-1.5">
      {OASIS_APPLICABILITY_OPTIONS.map((opt) => {
        const checked = selected.includes(opt);
        return (
          <div key={opt}>
            <label
              className={`flex items-center gap-2 text-[12px] text-slate-700 rounded-md border px-2.5 py-2 min-w-0 ${
                canEdit ? "cursor-pointer" : "cursor-default"
              } ${
                checked
                  ? "border-indigo-300 bg-indigo-50/60"
                  : "border-slate-200 bg-white"
              }`}
            >
              <input
                type="checkbox"
                className={`${checkboxClass} mt-0 shrink-0`}
                checked={checked}
                disabled={!canEdit}
                onChange={() => onToggle(opt)}
              />
              <span className="leading-snug break-words">{opt}</span>
            </label>
            {opt === OASIS_NOT_REQUIRED ? (
              <p className="mt-1 ml-1 text-[10px] italic text-slate-500">
                Single-visit quality episode / per CMS guidance
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

OasisApplicabilityGrid.propTypes = {
  values: PropTypes.arrayOf(PropTypes.string),
  canEdit: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

const PtDischargeNonVisitForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const buildPatientPrefill = useCallback(() => {
    const episodes = Array.isArray(patient?.episodes) ? patient.episodes : [];
    const latestEpisode = episodes[0];
    const soc = latestEpisode?.socDate || latestEpisode?.startDate || "";
    const certTo = latestEpisode?.endDate || latestEpisode?.toDate || "";
    const isPt = role === USER_ROLES.PT || role === USER_ROLES.PTA;
    const therapistName = isPt
      ? [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ")
      : "";

    return {
      ...emptyPtDischargeNonVisitPayload(),
      certPeriodFrom: soc ? formatDateForInput(soc) : "",
      certPeriodTo: certTo ? formatDateForInput(certTo) : "",
      dischargeDate: today(),
      disciplinePt: true,
      therapistName,
    };
  }, [patient, role, currentUser]);

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyPtDischargeNonVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};
      const legacyMigrated = migrateLegacyArrays(loadedPayload);

      const dischargeDate =
        data.documentationDate ||
        loadedPayload.dischargeDate ||
        loadedPayload.documentationDate ||
        data.dischargeDate ||
        today();

      const merged = {
        ...base,
        ...loadedPayload,
        ...legacyMigrated,
        ...pickPtVisitMeta(loadedPayload, data),
        dischargeDate,
        commonHeader: normalizePtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        certPeriodFrom: String(
          loadedPayload.certPeriodFrom || base.certPeriodFrom || "",
        ).trim(),
        certPeriodTo: String(
          loadedPayload.certPeriodTo || base.certPeriodTo || "",
        ).trim(),
        dischargeTime: String(loadedPayload.dischargeTime || "").trim(),
        disciplinePt: normalizeDisciplinePt(loadedPayload),
        therapistName: String(
          loadedPayload.therapistName || loadedPayload.clinicianName || "",
        ).trim(),
        credentialsLicense: String(
          loadedPayload.credentialsLicense || loadedPayload.credentials || "",
        ).trim(),
        nonVisitReasonOther: String(
          loadedPayload.nonVisitReasonOther || "",
        ).trim(),
        nonVisitExplanation: String(
          loadedPayload.nonVisitExplanation || "",
        ).trim(),
        dischargeDisposition: normalizeExclusiveOption(
          loadedPayload.dischargeDisposition,
          DISCHARGE_DISPOSITION_OPTIONS,
        ),
        dischargeDispositionOther: String(
          loadedPayload.dischargeDispositionOther || "",
        ).trim(),
        subsequentProviderName: String(
          loadedPayload.subsequentProviderName || "",
        ).trim(),
        subsequentProviderPhone: String(
          loadedPayload.subsequentProviderPhone || "",
        ).trim(),
        subsequentProviderAddress: String(
          loadedPayload.subsequentProviderAddress || "",
        ).trim(),
        functionalStatus: normalizeFunctionalStatus(
          loadedPayload.functionalStatus || emptyFunctionalStatusRows(),
        ),
        assistiveDeviceOther: String(
          loadedPayload.assistiveDeviceOther || "",
        ).trim(),
        weightBearingStatus: String(
          loadedPayload.weightBearingStatus || "",
        ).trim(),
        precautionsRestrictions: String(
          loadedPayload.precautionsRestrictions || "",
        ).trim(),
        dischargeReasonOther: String(
          loadedPayload.dischargeReasonOther || "",
        ).trim(),
        dischargeJustification: String(
          loadedPayload.dischargeJustification || "",
        ).trim(),
        planOfCareGoalStatus: normalizeExclusiveOption(
          loadedPayload.planOfCareGoalStatus,
          PLAN_OF_CARE_GOAL_OPTIONS,
        ),
        unresolvedProblems: String(
          loadedPayload.unresolvedProblems || "",
        ).trim(),
        recommendedContinuedCare: String(
          loadedPayload.recommendedContinuedCare || "",
        ).trim(),
        priorInstructionOther: String(
          loadedPayload.priorInstructionOther || "",
        ).trim(),
        physicianNotified: normalizeExclusiveYesNoNotRequired(
          loadedPayload.physicianNotified ||
            (Array.isArray(loadedPayload.physicianNotifyFlags)
              ? loadedPayload.physicianNotifyFlags
              : ""),
        ),
        physicianNotifyDate: String(
          loadedPayload.physicianNotifyDate || "",
        ).trim(),
        physicianNotifyTime: String(
          loadedPayload.physicianNotifyTime || "",
        ).trim(),
        physicianNotifyMethodOther: String(
          loadedPayload.physicianNotifyMethodOther || "",
        ).trim(),
        physicianNotifyPerson: String(
          loadedPayload.physicianNotifyPerson || "",
        ).trim(),
        physicianNotifyResponse: String(
          loadedPayload.physicianNotifyResponse || "",
        ).trim(),
        patientCaregiverContacted: normalizeExclusiveOption(
          loadedPayload.patientCaregiverContacted,
          PATIENT_CONTACTED_OPTIONS,
        ),
        caregiverCommDate: String(
          loadedPayload.caregiverCommDate || "",
        ).trim(),
        caregiverCommTime: String(
          loadedPayload.caregiverCommTime || "",
        ).trim(),
        caregiverCommMethodOther: String(
          loadedPayload.caregiverCommMethodOther || "",
        ).trim(),
        caregiverCommPerson: String(
          loadedPayload.caregiverCommPerson || "",
        ).trim(),
        dischargeInstructionOther: String(
          loadedPayload.dischargeInstructionOther || "",
        ).trim(),
        caregiverUnderstandingOther: String(
          loadedPayload.caregiverUnderstandingOther || "",
        ).trim(),
        medicationConcerns: String(
          loadedPayload.medicationConcerns || "",
        ).trim(),
        outstandingTreatments: normalizeExclusiveOption(
          loadedPayload.outstandingTreatments,
          OUTSTANDING_TREATMENT_OPTIONS,
        ),
        outstandingTreatmentDetails: String(
          loadedPayload.outstandingTreatmentDetails ||
            loadedPayload.outstandingOrders ||
            "",
        ).trim(),
        transitionInfoOther: String(
          loadedPayload.transitionInfoOther || "",
        ).trim(),
        transitionDate: String(loadedPayload.transitionDate || "").trim(),
        transitionMethod: String(loadedPayload.transitionMethod || "").trim(),
        transitionRecipient: String(
          loadedPayload.transitionRecipient || "",
        ).trim(),
        oasisApplicabilityOther: String(
          loadedPayload.oasisApplicabilityOther || "",
        ).trim(),
        oasisTimePoint: String(loadedPayload.oasisTimePoint || "").trim(),
        oasisComments: String(loadedPayload.oasisComments || "").trim(),
        finalClinicalSummary: String(
          loadedPayload.finalClinicalSummary || "",
        ).trim(),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      PT_DNV_ARRAY_KEYS.forEach((key) => {
        const source = legacyMigrated[key] ?? loadedPayload[key];
        merged[key] = Array.isArray(source)
          ? source.map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      LEGACY_PAYLOAD_KEYS.forEach((key) => {
        delete merged[key];
      });

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolvePtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Physical Therapy Discharge (Non-Visit)",
            formType: clinicalForm.formType || draft?.formType,
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            documentationDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: { ...buildPatientPrefill(), commonHeader },
          });
          return;
        }

        const res = await getPtDischargeNonVisitForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const episodePrefill = buildPatientPrefill();
        const commonHeader = await resolvePtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            certPeriodFrom:
              existingPayload.certPeriodFrom ||
              episodePrefill.certPeriodFrom ||
              "",
            certPeriodTo:
              existingPayload.certPeriodTo || episodePrefill.certPeriodTo || "",
            therapistName:
              existingPayload.therapistName ||
              existingPayload.clinicianName ||
              episodePrefill.therapistName ||
              "",
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const updateFunctionalTrend = (domain, trend) => {
    setValues((prev) => ({
      ...prev,
      functionalStatus: (prev.functionalStatus || []).map((row) =>
        row.domain === domain
          ? { ...row, trend: row.trend === trend ? "" : trend }
          : row,
      ),
    }));
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        PT_FORM_EDITOR_ROLES,
        "View only — only PT/PTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType,
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId:
      form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      dischargeDate,
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      ...ptFields
    } = values;

    const formattedDischarge = dischargeDate
      ? formatDateForInput(dischargeDate)
      : "";

    const nestedPayload = stripLegacyKeys(
      withNormalizedPtCommonHeader({
        ...ptFields,
        dischargeDate: formattedDischarge,
        certPeriodFrom: values.certPeriodFrom
          ? formatDateForInput(values.certPeriodFrom)
          : "",
        certPeriodTo: values.certPeriodTo
          ? formatDateForInput(values.certPeriodTo)
          : "",
        dischargeTime: String(values.dischargeTime || "").trim(),
        disciplinePt: Boolean(values.disciplinePt),
        therapistName: String(values.therapistName || "").trim(),
        credentialsLicense: String(values.credentialsLicense || "").trim(),
        nonVisitReasonOther: String(values.nonVisitReasonOther || "").trim(),
        nonVisitExplanation: String(values.nonVisitExplanation || "").trim(),
        dischargeDisposition: String(values.dischargeDisposition || "").trim(),
        dischargeDispositionOther: String(
          values.dischargeDispositionOther || "",
        ).trim(),
        subsequentProviderName: String(
          values.subsequentProviderName || "",
        ).trim(),
        subsequentProviderPhone: String(
          values.subsequentProviderPhone || "",
        ).trim(),
        subsequentProviderAddress: String(
          values.subsequentProviderAddress || "",
        ).trim(),
        functionalStatus: normalizeFunctionalStatus(values.functionalStatus),
        assistiveDeviceOther: String(values.assistiveDeviceOther || "").trim(),
        weightBearingStatus: String(values.weightBearingStatus || "").trim(),
        precautionsRestrictions: String(
          values.precautionsRestrictions || "",
        ).trim(),
        dischargeReasonOther: String(values.dischargeReasonOther || "").trim(),
        dischargeJustification: String(
          values.dischargeJustification || "",
        ).trim(),
        planOfCareGoalStatus: String(values.planOfCareGoalStatus || "").trim(),
        unresolvedProblems: String(values.unresolvedProblems || "").trim(),
        recommendedContinuedCare: String(
          values.recommendedContinuedCare || "",
        ).trim(),
        priorInstructionOther: String(
          values.priorInstructionOther || "",
        ).trim(),
        physicianNotified: normalizeExclusiveYesNoNotRequired(
          values.physicianNotified,
        ),
        physicianNotifyDate: values.physicianNotifyDate
          ? formatDateForInput(values.physicianNotifyDate)
          : "",
        physicianNotifyTime: String(values.physicianNotifyTime || "").trim(),
        physicianNotifyMethodOther: String(
          values.physicianNotifyMethodOther || "",
        ).trim(),
        physicianNotifyPerson: String(
          values.physicianNotifyPerson || "",
        ).trim(),
        physicianNotifyResponse: String(
          values.physicianNotifyResponse || "",
        ).trim(),
        patientCaregiverContacted: String(
          values.patientCaregiverContacted || "",
        ).trim(),
        caregiverCommDate: values.caregiverCommDate
          ? formatDateForInput(values.caregiverCommDate)
          : "",
        caregiverCommTime: String(values.caregiverCommTime || "").trim(),
        caregiverCommMethodOther: String(
          values.caregiverCommMethodOther || "",
        ).trim(),
        caregiverCommPerson: String(values.caregiverCommPerson || "").trim(),
        dischargeInstructionOther: String(
          values.dischargeInstructionOther || "",
        ).trim(),
        caregiverUnderstandingOther: String(
          values.caregiverUnderstandingOther || "",
        ).trim(),
        medicationConcerns: String(values.medicationConcerns || "").trim(),
        outstandingTreatments: String(
          values.outstandingTreatments || "",
        ).trim(),
        outstandingTreatmentDetails: String(
          values.outstandingTreatmentDetails || "",
        ).trim(),
        transitionInfoOther: String(values.transitionInfoOther || "").trim(),
        transitionDate: values.transitionDate
          ? formatDateForInput(values.transitionDate)
          : "",
        transitionMethod: String(values.transitionMethod || "").trim(),
        transitionRecipient: String(values.transitionRecipient || "").trim(),
        oasisApplicabilityOther: String(
          values.oasisApplicabilityOther || "",
        ).trim(),
        oasisTimePoint: String(values.oasisTimePoint || "").trim(),
        oasisComments: String(values.oasisComments || "").trim(),
        finalClinicalSummary: String(values.finalClinicalSummary || "").trim(),
      }),
    );

    PT_DNV_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    const payload = {
      flag,
      documentationDate: formattedDischarge || null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertPtVisitMetaForFlag(values, flag, {
      requireTimes: false,
    });
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const scheduleDate =
        formatDateForInput(values.dischargeDate) || today();
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updatePtDischargeNonVisitForm,
        filledByUserId,
        scheduleDate,
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showNonVisitOther = (values.nonVisitReasons || []).includes("Other");
  const showDispositionOther = values.dischargeDisposition === "Other";
  const showAssistiveOther = (values.assistiveDevices || []).includes("Other");
  const showDischargeReasonOther = (values.dischargeReasons || []).includes(
    "Other",
  );
  const showPriorInstructionOther = (values.priorInstructions || []).includes(
    "Other",
  );
  const showPhysicianMethodOther = (
    values.physicianNotifyMethods || []
  ).includes("Other");
  const showCaregiverMethodOther = (
    values.caregiverCommMethods || []
  ).includes("Other");
  const showInstructionTopicOther = (
    values.dischargeInstructionTopics || []
  ).includes("Other");
  const showUnderstandingOther = (values.caregiverUnderstanding || []).includes(
    "Other",
  );
  const showTransitionOther = (values.transitionInfo || []).includes("Other");
  const showOasisApplicabilityOther = (
    values.oasisApplicability || []
  ).includes("Other");

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Physical Therapy Discharge (Non-Visit)
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home Health Agency Documentation
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          caregiverSlot={caregiverSlot}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          dateLabel="Discharge Date"
          dateValue={displayDateUS(values.dischargeDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Discharge Date
                </div>
                <InputDatePicker
                  name="dischargeDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.dischargeDate) || ""}
                  onChange={(event) =>
                    setValue("dischargeDate", event.target.value)
                  }
                  max={DISCHARGE_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildPtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <PtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        {/* 7. Discharge Information */}
        <Section icon={LogOutIcon} title="7. Discharge Information">
          <div>
            <SubHeading>
              Reason this discharge is documented without a visit
            </SubHeading>
            <CheckboxGrid
              options={NON_VISIT_REASON_OPTIONS}
              values={values.nonVisitReasons}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("nonVisitReasons", opt)}
              columns={2}
            />
            {showNonVisitOther ? (
              <input
                type="text"
                className={`${fieldClass} mt-1.5`}
                value={values.nonVisitReasonOther || ""}
                disabled={!canEdit}
                placeholder="Specify other reason…"
                onChange={(e) =>
                  setValue("nonVisitReasonOther", e.target.value)
                }
              />
            ) : null}
          </div>

          <div className="mt-3">
            <Field label="Detailed Explanation">
              <textarea
                rows={4}
                className={textareaClass}
                value={values.nonVisitExplanation || ""}
                disabled={!canEdit}
                placeholder="Describe the circumstances requiring non-visit discharge documentation…"
                onChange={(e) =>
                  setValue("nonVisitExplanation", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        {/* 8. Discharge Disposition */}
        <Section icon={HelpingIcon} title="8. Discharge Disposition">
          <SubHeading>Where is the patient after discharge?</SubHeading>
          <ExclusiveChoiceGrid
            name="dischargeDisposition"
            options={DISCHARGE_DISPOSITION_OPTIONS}
            value={values.dischargeDisposition || ""}
            canEdit={canEdit}
            onChange={(opt) => {
              setValue("dischargeDisposition", opt);
              if (opt !== "Other") setValue("dischargeDispositionOther", "");
            }}
            columns={1}
          />
          {showDispositionOther ? (
            <input
              type="text"
              className={`${fieldClass} mt-1.5`}
              value={values.dischargeDispositionOther || ""}
              disabled={!canEdit}
              placeholder="Specify other disposition…"
              onChange={(e) =>
                setValue("dischargeDispositionOther", e.target.value)
              }
            />
          ) : null}

          <div className="mt-3 rounded-md border border-indigo-100 bg-indigo-50/50 px-3 py-3 space-y-2">
            <SubHeading>Subsequent Provider (if applicable)</SubHeading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
              <Field label="Provider Name">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.subsequentProviderName || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("subsequentProviderName", e.target.value)
                  }
                />
              </Field>
              <Field label="Phone">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.subsequentProviderPhone || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("subsequentProviderPhone", e.target.value)
                  }
                />
              </Field>
            </div>
            <Field label="Address">
              <textarea
                rows={2}
                className={textareaClass}
                value={values.subsequentProviderAddress || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("subsequentProviderAddress", e.target.value)
                }
              />
            </Field>
          </div>
        </Section>

        {/* 9 + 10 side-by-side on lg */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section icon={RunningIcon} title="9. Current Functional Status">
            <p className="text-[11px] italic text-slate-500 mb-2">
              Based on available information prior to non-visit discharge
            </p>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="min-w-full text-left text-[11px]">
                <thead>
                  <tr className="bg-slate-50 text-[9px] font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-2.5 py-2 w-[28%]">Domain</th>
                    {FUNCTIONAL_TREND_OPTIONS.map((opt) => (
                      <th key={opt} className="px-2 py-2 text-center">
                        {opt}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(values.functionalStatus || emptyFunctionalStatusRows()).map(
                    (row) => (
                      <tr
                        key={row.domain}
                        className="border-t border-slate-100"
                      >
                        <td className="px-2.5 py-2 font-medium text-slate-700">
                          {row.domain}
                        </td>
                        {FUNCTIONAL_TREND_OPTIONS.map((opt) => {
                          const checked = row.trend === opt;
                          return (
                            <td key={opt} className="px-2 py-2 text-center">
                              <input
                                type="radio"
                                name={`functional-trend-${row.domain}`}
                                className="h-3.5 w-3.5 border-slate-300 text-indigo-500"
                                checked={checked}
                                disabled={!canEdit}
                                onChange={() =>
                                  updateFunctionalTrend(row.domain, opt)
                                }
                                onClick={(e) => {
                                  if (canEdit && checked) {
                                    e.preventDefault();
                                    updateFunctionalTrend(row.domain, "");
                                  }
                                }}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-3">
              <SubHeading>Assistive Device</SubHeading>
              <CheckboxGrid
                options={ASSISTIVE_DEVICE_OPTIONS}
                values={values.assistiveDevices}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("assistiveDevices", opt)}
                columns={3}
              />
              {showAssistiveOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.assistiveDeviceOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other device…"
                  onChange={(e) =>
                    setValue("assistiveDeviceOther", e.target.value)
                  }
                />
              ) : null}
            </div>

            <div className="mt-3">
              <Field label="Weight Bearing Status">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.weightBearingStatus || ""}
                  disabled={!canEdit}
                  placeholder="e.g. FWB, PWB, NWB…"
                  onChange={(e) =>
                    setValue("weightBearingStatus", e.target.value)
                  }
                />
              </Field>
            </div>
            <div className="mt-3">
              <Field label="Precautions / Restrictions">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.precautionsRestrictions || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("precautionsRestrictions", e.target.value)
                  }
                />
              </Field>
            </div>
          </Section>

          <Section icon={AlertCircleIcon} title="10. Reason for Discharge">
            <p className="text-[11px] text-slate-500 mb-2">
              Select all that apply
            </p>
            <CheckboxGrid
              options={DISCHARGE_REASON_OPTIONS}
              values={values.dischargeReasons}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("dischargeReasons", opt)}
              columns={1}
            />
            {showDischargeReasonOther ? (
              <input
                type="text"
                className={`${fieldClass} mt-1.5`}
                value={values.dischargeReasonOther || ""}
                disabled={!canEdit}
                placeholder="Specify other reason…"
                onChange={(e) =>
                  setValue("dischargeReasonOther", e.target.value)
                }
              />
            ) : null}
            <div className="mt-3">
              <Field label="Discharge Justification / Narrative">
                <textarea
                  rows={5}
                  className={textareaClass}
                  value={values.dischargeJustification || ""}
                  disabled={!canEdit}
                  placeholder="Document clinical justification for discharge…"
                  onChange={(e) =>
                    setValue("dischargeJustification", e.target.value)
                  }
                />
              </Field>
            </div>
          </Section>
        </div>

        {/* 11. Plan of Care / Goals */}
        <Section icon={TargetIcon} title="11. Plan of Care / Goals">
          <SubHeading>Status of PT Goals</SubHeading>
          <PlanOfCareGoalChoice
            value={values.planOfCareGoalStatus || ""}
            canEdit={canEdit}
            onChange={(opt) => setValue("planOfCareGoalStatus", opt)}
          />

          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Field label="Unresolved Problems">
              <textarea
                rows={4}
                className={textareaClass}
                value={values.unresolvedProblems || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("unresolvedProblems", e.target.value)}
              />
            </Field>
            <Field label="Recommended Continued Care">
              <textarea
                rows={4}
                className={textareaClass}
                value={values.recommendedContinuedCare || ""}
                disabled={!canEdit}
                onChange={(e) =>
                  setValue("recommendedContinuedCare", e.target.value)
                }
              />
            </Field>
          </div>

          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60 px-3 py-3">
            <SubHeading>
              Patient / Caregiver Instructed In (Prior to Discharge)
            </SubHeading>
            <CheckboxGrid
              options={PRIOR_INSTRUCTION_OPTIONS}
              values={values.priorInstructions}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("priorInstructions", opt)}
              columns={2}
            />
            {showPriorInstructionOther ? (
              <input
                type="text"
                className={`${fieldClass} mt-1.5`}
                value={values.priorInstructionOther || ""}
                disabled={!canEdit}
                placeholder="Specify other instruction…"
                onChange={(e) =>
                  setValue("priorInstructionOther", e.target.value)
                }
              />
            ) : null}
          </div>
        </Section>

        {/* 12. Communication */}
        <Section icon={CommentsIcon} title="12. Communication">
          <div className="space-y-4">
            <div className="rounded-md border border-slate-200 px-3 py-3">
              <SubHeading>
                Physician / Allowed Practitioner Notification
              </SubHeading>
              <div className="mt-1.5 max-w-md">
                <ExclusiveChoiceGrid
                  name="physicianNotified"
                  options={YES_NO_NOT_REQUIRED_OPTIONS}
                  value={values.physicianNotified || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("physicianNotified", opt)}
                  columns={3}
                />
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Date">
                  <DateFieldInput
                    name="physicianNotifyDate"
                    value={values.physicianNotifyDate}
                    canEdit={canEdit}
                    printMode={printMode}
                    onChange={(v) => setValue("physicianNotifyDate", v)}
                    max={DISCHARGE_DATE_MAX}
                  />
                </Field>
                <Field label="Time">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.physicianNotifyTime || ""}
                    disabled={!canEdit}
                    placeholder="e.g. 10:30 AM"
                    onChange={(e) =>
                      setValue("physicianNotifyTime", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="mt-3">
                <SubHeading>Method(s)</SubHeading>
                <CheckboxGrid
                  options={PHYSICIAN_NOTIFY_METHOD_OPTIONS}
                  values={values.physicianNotifyMethods}
                  canEdit={canEdit}
                  onToggle={(opt) =>
                    toggleListValue("physicianNotifyMethods", opt)
                  }
                  columns={2}
                />
                {showPhysicianMethodOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.physicianNotifyMethodOther || ""}
                    disabled={!canEdit}
                    placeholder="Specify other method…"
                    onChange={(e) =>
                      setValue("physicianNotifyMethodOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Person Contacted">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.physicianNotifyPerson || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("physicianNotifyPerson", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="mt-3">
                <Field label="Response / Orders Received">
                  <textarea
                    rows={3}
                    className={textareaClass}
                    value={values.physicianNotifyResponse || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("physicianNotifyResponse", e.target.value)
                    }
                  />
                </Field>
              </div>
            </div>

            <div className="rounded-md border border-slate-200 px-3 py-3">
              <SubHeading>Patient / Caregiver Communication</SubHeading>
              <div className="mt-1.5">
                <ExclusiveChoiceGrid
                  name="patientCaregiverContacted"
                  options={PATIENT_CONTACTED_OPTIONS}
                  value={values.patientCaregiverContacted || ""}
                  canEdit={canEdit}
                  onChange={(opt) =>
                    setValue("patientCaregiverContacted", opt)
                  }
                  columns={2}
                />
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
                <Field label="Date">
                  <DateFieldInput
                    name="caregiverCommDate"
                    value={values.caregiverCommDate}
                    canEdit={canEdit}
                    printMode={printMode}
                    onChange={(v) => setValue("caregiverCommDate", v)}
                    max={DISCHARGE_DATE_MAX}
                  />
                </Field>
                <Field label="Time">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.caregiverCommTime || ""}
                    disabled={!canEdit}
                    placeholder="e.g. 10:30 AM"
                    onChange={(e) =>
                      setValue("caregiverCommTime", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="mt-3">
                <SubHeading>Method(s)</SubHeading>
                <CheckboxGrid
                  options={CAREGIVER_COMM_METHOD_OPTIONS}
                  values={values.caregiverCommMethods}
                  canEdit={canEdit}
                  onToggle={(opt) =>
                    toggleListValue("caregiverCommMethods", opt)
                  }
                  columns={2}
                />
                {showCaregiverMethodOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.caregiverCommMethodOther || ""}
                    disabled={!canEdit}
                    placeholder="Specify other method…"
                    onChange={(e) =>
                      setValue("caregiverCommMethodOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
              <div className="mt-3">
                <Field label="Person Contacted">
                  <input
                    type="text"
                    className={fieldClass}
                    value={values.caregiverCommPerson || ""}
                    disabled={!canEdit}
                    onChange={(e) =>
                      setValue("caregiverCommPerson", e.target.value)
                    }
                  />
                </Field>
              </div>
              <div className="mt-3">
                <SubHeading>Discharge Instruction Topics</SubHeading>
                <CheckboxGrid
                  options={DISCHARGE_INSTRUCTION_TOPIC_OPTIONS}
                  values={values.dischargeInstructionTopics}
                  canEdit={canEdit}
                  onToggle={(opt) =>
                    toggleListValue("dischargeInstructionTopics", opt)
                  }
                  columns={2}
                />
                {showInstructionTopicOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.dischargeInstructionOther || ""}
                    disabled={!canEdit}
                    placeholder="Specify other topic…"
                    onChange={(e) =>
                      setValue("dischargeInstructionOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
              <div className="mt-3">
                <SubHeading>Caregiver Understanding</SubHeading>
                <CheckboxGrid
                  options={UNDERSTANDING_OPTIONS}
                  values={values.caregiverUnderstanding}
                  canEdit={canEdit}
                  onToggle={(opt) =>
                    toggleListValue("caregiverUnderstanding", opt)
                  }
                  columns={2}
                />
                {showUnderstandingOther ? (
                  <input
                    type="text"
                    className={`${fieldClass} mt-1.5`}
                    value={values.caregiverUnderstandingOther || ""}
                    disabled={!canEdit}
                    placeholder="Specify other…"
                    onChange={(e) =>
                      setValue("caregiverUnderstandingOther", e.target.value)
                    }
                  />
                ) : null}
              </div>
            </div>
          </div>
        </Section>

        {/* 13 + 14 side-by-side on lg */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Section icon={PillIcon} title="13. Medication / Treatment Information">
            <CheckboxGrid
              options={MEDICATION_RECONCILE_OPTIONS}
              values={values.medicationReconcile}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("medicationReconcile", opt)}
              columns={1}
            />
            <div className="mt-3">
              <Field label="Medication Concerns / Issues Noted Prior to Discharge">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.medicationConcerns || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("medicationConcerns", e.target.value)
                  }
                />
              </Field>
            </div>
            <div className="mt-3">
              <SubHeading>Outstanding Treatments</SubHeading>
              <ExclusiveChoiceGrid
                name="outstandingTreatments"
                options={OUTSTANDING_TREATMENT_OPTIONS}
                value={values.outstandingTreatments || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("outstandingTreatments", opt)}
                columns={3}
              />
            </div>
            <div className="mt-3">
              <Field label="Outstanding Treatment Details">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.outstandingTreatmentDetails || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("outstandingTreatmentDetails", e.target.value)
                  }
                />
              </Field>
            </div>
          </Section>

          <Section icon={HeartPulseIcon} title="14. Transition / Handoff">
            <SubHeading>Information Communicated</SubHeading>
            <CheckboxGrid
              options={TRANSITION_INFO_OPTIONS}
              values={values.transitionInfo}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("transitionInfo", opt)}
              columns={1}
            />
            {showTransitionOther ? (
              <input
                type="text"
                className={`${fieldClass} mt-1.5`}
                value={values.transitionInfoOther || ""}
                disabled={!canEdit}
                placeholder="Specify other information…"
                onChange={(e) =>
                  setValue("transitionInfoOther", e.target.value)
                }
              />
            ) : null}
            <div className="mt-3 grid grid-cols-1 gap-y-2">
              <Field label="Transition Date">
                <DateFieldInput
                  name="transitionDate"
                  value={values.transitionDate}
                  canEdit={canEdit}
                  printMode={printMode}
                  onChange={(v) => setValue("transitionDate", v)}
                  max={DISCHARGE_DATE_MAX}
                />
              </Field>
              <Field label="Method">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.transitionMethod || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("transitionMethod", e.target.value)
                  }
                />
              </Field>
              <Field label="Recipient">
                <input
                  type="text"
                  className={fieldClass}
                  value={values.transitionRecipient || ""}
                  disabled={!canEdit}
                  onChange={(e) =>
                    setValue("transitionRecipient", e.target.value)
                  }
                />
              </Field>
            </div>
          </Section>
        </div>

        {/* 15. OASIS / CMS Assessment Review */}
        <Section icon={FileCheckIcon} title="15. OASIS / CMS Assessment Review">
          <SubHeading>OASIS Applicability</SubHeading>
          <OasisApplicabilityGrid
            values={values.oasisApplicability}
            canEdit={canEdit}
            onToggle={(opt) => toggleListValue("oasisApplicability", opt)}
          />
          {showOasisApplicabilityOther ? (
            <input
              type="text"
              className={`${fieldClass} mt-1.5`}
              value={values.oasisApplicabilityOther || ""}
              disabled={!canEdit}
              placeholder="Specify other…"
              onChange={(e) =>
                setValue("oasisApplicabilityOther", e.target.value)
              }
            />
          ) : null}

          <div className="mt-3">
            <SubHeading>Assessment Status</SubHeading>
            <CheckboxGrid
              options={OASIS_ASSESSMENT_STATUS_OPTIONS}
              values={values.oasisAssessmentStatus}
              canEdit={canEdit}
              onToggle={(opt) =>
                toggleListValue("oasisAssessmentStatus", opt)
              }
              columns={2}
            />
          </div>

          <div className="mt-3 max-w-md">
            <Field label="OASIS Time Point">
              <input
                type="text"
                className={fieldClass}
                value={values.oasisTimePoint || ""}
                disabled={!canEdit}
                placeholder="e.g. Transfer, Discharge…"
                onChange={(e) => setValue("oasisTimePoint", e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3">
            <SubHeading>Submission</SubHeading>
            <CheckboxGrid
              options={OASIS_SUBMISSION_OPTIONS}
              values={values.oasisSubmission}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("oasisSubmission", opt)}
              columns={2}
            />
          </div>

          <div className="mt-3">
            <Field label="Comments">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.oasisComments || ""}
                disabled={!canEdit}
                onChange={(e) => setValue("oasisComments", e.target.value)}
              />
            </Field>
          </div>

          <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-3">
            <p className="text-[12px] leading-relaxed text-indigo-950">
              {OASIS_CMS_CALLOUT}
            </p>
          </div>
        </Section>

        {/* 16. Final PT Summary */}
        <Section icon={MedicalNotesIcon} title="16. Final PT Summary">
          <Field label="PT Final Summary / Clinical Impression">
            <textarea
              rows={8}
              className={textareaClass}
              value={values.finalClinicalSummary || ""}
              disabled={!canEdit}
              placeholder="Document final skilled PT summary and clinical impression…"
              onChange={(e) =>
                setValue("finalClinicalSummary", e.target.value)
              }
            />
          </Field>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          showPatientSection={false}
          clinicianNameValue={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          canSign={canSign}
          showClinicianSection={!permissions.hideClinicalSignature}
          printMode={printMode}
          clinicianSectionTitle="PT / PTA Authentication"
        />

        {isReviewer && !printMode && (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        )}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

PtDischargeNonVisitForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default PtDischargeNonVisitForm;
